import { ref } from 'vue';
import { PleasedChat } from '@/pleased-sdk/PleasedChat/PleasedChat';
import { analytics, AI_EVENTS } from '@/common/logs';
import type { ConnectToAgent } from '@/common/bot/types/config';
import { logger } from '@/common/api/logger';

export function useInterveneChat() {
  const cleanupInterventionListener = ref<(() => void) | null>(null);

  /**
   * Sets up Firebase listener for AI chat intervention
   * @param params - Configuration for AI intervention setup
   */
  async function setupFirebaseAiChat(params: {
    firebaseToken: string;
    connectConfig: ConnectToAgent;
    /** Same identity rules as {@link AiService.handleAiChatCreation}: member uses user id/token, guest uses guest id/token. */
    user: {
      userId: string | number | undefined;
      accessToken: string | undefined;
    };
    onIntervene: (chatId: number) => void;
  }) {
    const { firebaseToken, connectConfig, user, onIntervene } = params;

    const customerId = user.userId != null && user.userId !== '' ? Number(user.userId) : null;

    if (customerId == null || !Number.isFinite(customerId) || !firebaseToken || !user.accessToken) {
      logger.warn('Cannot setup AI intervention: missing customer id, firebase token, or access token', {
        customerId,
        hasFirebaseToken: Boolean(firebaseToken),
        hasAccessToken: Boolean(user.accessToken),
      });
      return;
    }

    const aiSessionId = connectConfig?.resumeAiSessionId || analytics.sessionId || '';

    if (!aiSessionId) {
      logger.warn('Cannot setup AI intervention: no AI session ID');
      return;
    }

    // Clean up any existing listener
    cleanup();

    // Setup intervention listener using the new PleasedChat static method
    cleanupInterventionListener.value = PleasedChat.setupAiChatIntervention(
      aiSessionId,
      firebaseToken,
      handleInterveneAiChat(onIntervene)
    );
  }

  /**
   * Handles AI chat intervention event
   */
  function handleInterveneAiChat(callback: (chatId: number) => void) {
    return (chatId: number) => {
      analytics.track({
        name: AI_EVENTS.AI_INTERVENE_CHAT,
        metadata: { chatId },
      });

      callback(chatId);
    };
  }

  /**
   * Cleans up intervention listener
   */
  function cleanup() {
    if (cleanupInterventionListener.value) {
      cleanupInterventionListener.value();
      cleanupInterventionListener.value = null;
    }
  }

  return {
    setupFirebaseAiChat,
    cleanup,
  };
}
